import { useEffect, useState } from "react";
import axios from "axios";
import { useAccountant } from "@/contexts/AccountantContext";
import { formatMoneyCompact } from "@/lib/formatMoney";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import KipaIcon from "@/components/kipa/KipaIcon";
import { AlertTriangle, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForecastCard({ reloadKey }) {
  const { fmt, currency } = useAccountant();
  const fmtShort = (n) => formatMoneyCompact(n, currency);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/forecast`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.message));
  }, [reloadKey]);

  if (err) {
    return (
      <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-4 card-sh text-[12px] text-[var(--tx3)]" data-testid="forecast-error">
        Forecast unavailable: {err}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-4 card-sh text-[12px] text-[var(--tx3)]" data-testid="forecast-loading">
        Calculating forecast…
      </div>
    );
  }

  const hasData = (data.mtd_revenue || 0) > 0 || (data.mtd_expenses || 0) > 0;
  const growthPositive = data.projected_growth_pct >= 0;
  const dailyRate = data.daily_run_rate || 0;

  // Build the trend with a projection — concat historical + projection
  const trend = (data.trend || []).map((r) => ({
    date: r.date.slice(5),
    profit: Math.round(r.profit),
    projected: null,
  }));
  // Pad out rest of month with linear projection line starting from last point
  if (hasData && data.days_remaining > 0 && trend.length > 0) {
    const last = trend[trend.length - 1];
    // Change last point's `projected` to current profit so line is continuous
    last.projected = last.profit;
    // Project: each remaining day add daily rate
    for (let i = 1; i <= data.days_remaining; i++) {
      const projProfit = last.profit + dailyRate * i;
      const date = new Date();
      date.setDate(data.day_of_month + i);
      trend.push({
        date: `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
        profit: null,
        projected: Math.round(projProfit),
      });
    }
  }

  return (
    <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-4 card-sh" data-testid="forecast-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[13px] font-extrabold text-[var(--tx)] flex items-center gap-1.5">
            <KipaIcon icon={Sparkles} size="sm" color="var(--color-primary)" />
            Cash-flow Forecast
          </div>
          <div className="text-[11px] text-[var(--tx3)] mt-0.5">
            Based on your pace so far in {data.month}
          </div>
        </div>
        <div
          className="text-[10px] font-bold px-2 py-1 rounded-full"
          style={{
            background: growthPositive ? "var(--gl)" : "var(--rl)",
            color: growthPositive ? "var(--gn)" : "var(--rd)",
          }}
          data-testid="forecast-growth"
        >
          {growthPositive ? "↑" : "↓"} {Math.abs(data.projected_growth_pct).toFixed(1)}% vs last month
        </div>
      </div>

      {!hasData ? (
        <div className="text-center py-6 text-[var(--tx3)] text-[12px]" data-testid="forecast-empty">
          Record a few transactions this month and I'll project where you'll land by month-end.
        </div>
      ) : (
        <>
          {/* Hero projected profit */}
          <div
            className="rounded-2xl p-4 mb-3"
            style={{
              background: "var(--ac)",
              color: "#fff",
            }}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/70 mb-1">
              Projected End-of-Month Profit
            </div>
            <div className="text-[30px] font-black leading-none mb-1" data-testid="forecast-projected-profit">
              {fmt(data.projected_profit)}
            </div>
            <div className="text-[11px] text-white/85 font-semibold">
              Day {data.day_of_month} of {data.total_days} · {data.days_remaining} day{data.days_remaining !== 1 ? "s" : ""} remaining
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Stat label="So Far" value={fmt(data.mtd_profit)} tone="tx" testid="forecast-mtd-profit" />
            <Stat label="Daily Rate" value={fmt(dailyRate)} tone="bl" testid="forecast-daily-rate" />
            <Stat label="Projected Revenue" value={fmtShort(data.projected_revenue)} tone="gn" testid="forecast-projected-revenue" />
          </div>

          {/* Projection chart */}
          <div style={{ width: "100%", height: 140, minHeight: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="histFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#166534" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#166534" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="projFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0D6E6E" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0D6E6E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9, fill: "#8A847E" }}
                  axisLine={false} tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 9, fill: "#8A847E" }}
                  tickFormatter={fmtShort}
                  axisLine={false} tickLine={false}
                  width={40}
                />
                <Tooltip
                  formatter={(v) => v === null ? "—" : fmt(v)}
                  contentStyle={{ borderRadius: 12, border: "1px solid #E7E5DF", fontSize: 12 }}
                />
                <Area type="monotone" dataKey="profit" name="Actual" stroke="#166534" strokeWidth={2} fill="url(#histFill)" connectNulls={false} />
                <Area type="monotone" dataKey="projected" name="Projected" stroke="#0D6E6E" strokeWidth={2} strokeDasharray="4 4" fill="url(#projFill)" connectNulls={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Budget pace warnings */}
          {data.budget_pace && data.budget_pace.filter((b) => b.projected_status === "over").length > 0 && (
            <div className="mt-3 pt-3 border-t border-[var(--br)]">
              <div className="text-[11px] font-bold text-[var(--rd)] mb-2 flex items-center gap-1.5" data-testid="forecast-budget-warnings">
                <KipaIcon icon={AlertTriangle} size="xs" color="var(--color-error)" />
                Budgets at risk
              </div>
              {data.budget_pace
                .filter((b) => b.projected_status === "over")
                .slice(0, 3)
                .map((b) => (
                  <div key={b.category} className="flex justify-between text-[11px] mb-1 last:mb-0">
                    <span className="text-[var(--tx2)] capitalize">{b.category}</span>
                    <span className="text-[var(--rd)] font-bold">
                      Projected over by {fmt(b.projected_over_by)}
                    </span>
                  </div>
                ))}
            </div>
          )}

          <div className="text-[10px] text-[var(--tx3)] mt-3 italic leading-snug">
            This is a linear projection based on your daily pace so far. Actual results may vary.
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const color = {
    tx: "var(--tx)",
    bl: "var(--bl)",
    gn: "var(--gn)",
    rd: "var(--rd)",
  }[tone] || "var(--tx)";
  return (
    <div className="bg-[var(--sf2)] rounded-xl p-2" data-testid={testid}>
      <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--tx3)]">{label}</div>
      <div className="font-extrabold text-[13px]" style={{ color }}>{value}</div>
    </div>
  );
}
